import type { GroupingSchemaType, IconsAlignEnum } from '@vedalib/editor/lib/brand'
import { SCHEMA } from '@vedalib/editor/lib/brand'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import { useCallback, useMemo } from 'react'
import React from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import type { IFieldConfig } from 'components/formKit'
import {
  FieldColorPicker,
  FieldFileId,
  FieldNumber,
  FieldNumberArray,
  FieldRadio,
  FieldSegmented,
  FieldSlider,
  FormField,
} from 'components/formKit'
import { useFormData } from 'components/formKit/hooks'
import { TileButton } from 'components/uiKit/Button'
import { Typography } from 'components/uiKit/Typography'
import { IDENTITY } from 'constants/commonConstans'
import { BrandFieldShadow, BrandTagEdited } from 'services/Branding/components'
import {
  BORDER_OPTIONS,
  OFFSET_ARRAY,
  VERTICAL_ALIGN_OPTIONS,
  ICON_ALIGN_OPTIONS,
} from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { isAnyEdited } from 'services/Branding/fieldConfigs/utils'
import { genBrandField, genSchema } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import type { TestGroupingFormBrandType } from './testGroupingTypes'

const useShowAnswerBorder = ({ name, getter = IDENTITY }: IFieldConfig) => {
  const data = useFormData<GroupingSchemaType>(name, 2)
  const border = getter(data?.answer.borderStyle)

  return useMemo(() => ({ hidden: border === 'none' }), [border])
}

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.testGrouping.style, {
  common: {
    height: genBrandField({
      as: FieldNumber,
      label: t('input.label.height'),
      props: { width: 64 },
    }),
    width: genBrandField({
      as: FieldNumber,
      label: t('input.label.width'),
      props: { width: 64 },
    }),
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.testGrouping.schema.navIcons'),
      useConfigs: [useBrandColorOptions],
    }),
  },
  answer: {
    position: genBrandField({
      as: FieldSegmented<'top' | 'bottom'>,
      label: t('elements.testGrouping.schema.answerPosition'),
      props: {
        options: [
          { label: t('elements.testGrouping.schema.positionTop'), value: 'top' },
          { label: t('elements.testGrouping.schema.positionBottom'), value: 'bottom' },
        ],
      },
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    alignItems: genBrandField({
      as: FieldRadio<'flex-start' | 'center' | 'flex-end'>,
      label: t('input.label.alignment'),
      props: {
        options: VERTICAL_ALIGN_OPTIONS,
        hideRadio: true,
        fluid: false,
        direction: 'horizontal',
      },
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowAnswerBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowAnswerBorder],
    }),
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.testGrouping.schema.answerBoxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
  answerNavigation: {
    iconsAlign: genBrandField({
      as: FieldRadio<IconsAlignEnum>,
      label: t('elements.testGrouping.schema.navPosition'),
      props: {
        fluid: false,
        hideRadio: true,
        direction: 'horizontal',
        options: ICON_ALIGN_OPTIONS,
      },
    }),
  },
  dragButton: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      info: t('elements.testGrouping.schema.dragHint'),
      useConfigs: [useBrandColorOptions],
    }),
    height: genBrandField({
      as: FieldNumber,
      label: t('input.label.height'),
      props: { width: 64 },
    }),
    width: genBrandField({
      as: FieldNumber,
      label: t('input.label.width'),
      props: { width: 64 },
    }),
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.testGrouping.schema.dragIcon'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
    }),
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.iconColor'),
      useConfigs: [useBrandColorOptions],
    }),
    iconsAlign: genBrandField({
      as: FieldRadio<IconsAlignEnum>,
      label: t('elements.card.tags.sides.button.position'),
      props: {
        fluid: false,
        hideRadio: true,
        direction: 'horizontal',
        options: ICON_ALIGN_OPTIONS,
      },
    }),
  },
  hover: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.hoverColor'),
      props: { transparent: true, labeled: true, resettable: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  group: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    alignItems: genBrandField({
      as: FieldRadio<'flex-start' | 'center' | 'flex-end'>,
      label: t('input.label.alignment'),
      props: {
        options: VERTICAL_ALIGN_OPTIONS,
        hideRadio: true,
        fluid: false,
        direction: 'horizontal',
      },
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.testGrouping.schema.groupBoxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
  groupNavigation: {
    iconsAlign: genBrandField({
      as: FieldRadio<IconsAlignEnum>,
      label: t('elements.card.tags.sides.button.position'),
      props: {
        fluid: false,
        hideRadio: true,
        direction: 'horizontal',
        options: ICON_ALIGN_OPTIONS,
      },
    }),
  },
  progress: {
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.progressBackground'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    height: genBrandField({
      as: FieldNumber,
      label: t('input.label.height'),
      rules: [{ min: 1, message: ' ' }],
      props: { width: 64 },
    }),
  },
  progressResult: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.progressCompleted'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  indents: {
    padding: genBrandField({
      as: FieldNumberArray,
      label: t('elements.grouping.schema.cardIndents'),
      layout: 'vertical',
      props: { options: OFFSET_ARRAY },
    }),
    gap: genBrandField({
      as: FieldNumber,
      label: t('elements.testGrouping.schema.gap'),
      props: { width: 64 },
    }),
    marginTop: genBrandField({
      as: FieldNumber,
      label: t('elements.testGrouping.schema.marginTop'),
      props: { width: 64 },
    }),
  },
})

const TestGroupingFormStyle: TestGroupingFormBrandType = ({
  device,
  subConfigs,
  edited,
  setForm,
}) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  const handleSetFeedback = useCallback(() => setForm?.(BrandFormEnum.FEEDBACK), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom gap sides>
        <Typography weight='medium'>{t('elements.testGrouping.tags.common')}</Typography>
        <FormField config={schema.common.height} />
        <FormField config={schema.common.width} />
        <FormField config={schema.common.borderRadius} />
        <FormField config={schema.common.color} />

        <Typography weight='medium'>{t('elements.testGrouping.tags.answer')}</Typography>
        <FormField config={schema.answer.position} />
        <FormField config={schema.answer.backgroundColor} />
        <FormField config={schema.answer.alignItems} />
        <FormField config={schema.answer.borderStyle} />
        <FormField config={schema.answer.borderColor} />
        <FormField config={schema.answer.borderWidth} />
        <FormField config={schema.answerNavigation.iconsAlign} />
        <FormField config={schema.answer.boxShadow} />

        <Typography weight='medium'>{t('elements.testGrouping.tags.dragButton')}</Typography>
        <FormField config={schema.dragButton.backgroundColor} />
        <FormField config={schema.hover.backgroundColor} />
        <FormField config={schema.dragButton.height} />
        <FormField config={schema.dragButton.width} />
        <FormField config={schema.dragButton.backgroundImage} />
        <FormField config={schema.dragButton.color} />
        <FormField config={schema.dragButton.iconsAlign} />

        <Typography weight='medium'>{t('elements.testGrouping.tags.group')}</Typography>
        <FormField config={schema.group.backgroundColor} />
        <FormField config={schema.group.alignItems} />
        <FormField config={schema.group.borderStyle} />
        <FormField config={schema.group.borderColor} />
        <FormField config={schema.group.borderWidth} />
        <FormField config={schema.group.boxShadow} />

        <Typography weight='medium'>{t('elements.testGrouping.tags.progress')}</Typography>
        <FormField config={schema.progress.borderRadius} />
        <FormField config={schema.progress.backgroundColor} />
        <FormField config={schema.progress.height} />
        <FormField config={schema.progressResult.backgroundColor} />

        <Typography weight='medium'>{t('elements.testGrouping.tags.indents')}</Typography>
        <FormField config={schema.indents.padding} />
        <FormField config={schema.indents.gap} />
        <FormField config={schema.indents.marginTop} />

        <TileButton
          description={t('uiKit.button.richTextDescription')}
          icon='otherRight'
          name='showFontForm'
          onClick={handleSetTypography}
        >
          <Typography weight='medium'>{t('page.brand.tab.typography')}</Typography>
          {isAnyEdited(edited.font) && <BrandTagEdited />}
        </TileButton>

        <TileButton
          description={t('uiKit.button.feedbackDescription')}
          icon='otherRight'
          name='showFeedbackForm'
          onClick={handleSetFeedback}
        >
          <Typography weight='medium'>{t('page.brand.tab.feedback')}</Typography>
          {isAnyEdited(edited.feedback) && <BrandTagEdited />}
        </TileButton>
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(TestGroupingFormStyle) as typeof TestGroupingFormStyle
